import { MapMouseEvent } from 'maplibre-gl';
import { WeatherPickingInfoLayer } from '../../manager/types';
import { getPalette, Palette } from '../../palettes/palettes';
import products from '../../products/products';
import { HeatmapStyle, WeatherSettingsChangeSource } from '../../settings/settings';
import { generateRampFromPalette } from '../../utils/ramp';
import { MessageType } from '../../worker/actor/actor-messages';
import { ImageLayer } from '../custom/image/image-layer';
import { WeatherLayerBase } from '../weather-layer-base';

export class HeatmapLayer extends WeatherLayerBase {
    private layer: ImageLayer | null = null;
    private response:
        | {
              data: Uint8Array;
              bounds: number[];
              width: number;
              height: number;
          }
        | null
        | undefined = null;

    private palette: Palette | null = null;
    private currentHeatmapStyle: HeatmapStyle = HeatmapStyle.none;

    protected onDisplayChanged(): void {
        this.map.setLayoutProperty(this.baseId, 'visibility', this.shouldDisplay() ? 'visible' : 'none');
    }

    protected async loadFrame(frameId: number) {
        this.layer!.opacity = this.settings.winds.heatmapOpacity;
        this.response = await this.dispatcher.dispatch({
            type: this.settings.winds.heatmapStyle === HeatmapStyle.temperature ? MessageType.getTempHeatmap : MessageType.getWindHeatmap,
            data: {
                id: this.productId,
                frame: frameId,
                workspaceId: this.workspaceId
            }
        });

        if (!this.response) {
            return;
        }

        this.layer?.updateBounds(this.response.bounds as [number, number, number, number]);
        this.layer?.updateImage(this.response.data, this.response.width, this.response.height, 'linear');
    }

    protected async loadProduct(): Promise<void> {
        const code = this.info!.frames[0].header.productPallet;
        const palette = getPalette(code, this.settings);
        this.palette = palette;
        const ramp = generateRampFromPalette(palette!);

        this.layer?.updateRamp(ramp, 'linear');
    }

    protected onAdd(): void {
        this.layer = new ImageLayer(this.baseId);
        this.layer.minLevel = -1; // This needs to be -1 to ensure rendering points having speed 0
        this.layer.opacity = this.settings.winds.heatmapOpacity;
        this.map.addLayer(this.layer, this.slotTopLayerId);
    }

    protected onRemove(): void {
        this.map.removeLayer(this.baseId);
    }

    protected onSettingsUpdated(source: WeatherSettingsChangeSource): void {
        if (!this.layer) {
            return;
        }
        if (source === WeatherSettingsChangeSource.windsHeatmapTransparency) {
            this.layer.opacity = this.settings.winds.heatmapOpacity;
        } else if (source === WeatherSettingsChangeSource.winds) {
            this.loadProduct();
        } else if (source === WeatherSettingsChangeSource.windsHeatmapStyle) {
            if (this.currentHeatmapStyle !== this.settings.winds.heatmapStyle) {
                this.currentHeatmapStyle = this.settings.winds.heatmapStyle;
                this.loadProduct();
                if (this.visibleFrame != null) {
                    this.loadFrame(this.visibleFrame);
                }
            }
        }
    }

    async pickObjects(event: MapMouseEvent): Promise<WeatherPickingInfoLayer | null> {
        if (!this.response || !this.palette) {
            return null;
        }

        const lngLat = event.lngLat;
        const lngWidth = this.response.bounds[2] - this.response.bounds[0];
        const latHeight = this.response.bounds[3] - this.response.bounds[1];

        const colPerDeg = this.response.width / lngWidth;
        const rowPerDeg = this.response.height / latHeight;
        const x = Math.round((lngLat.lng - this.response.bounds[0]) * colPerDeg);
        const y = Math.round((lngLat.lat - this.response.bounds[1]) * rowPerDeg);

        if (x < 0 || x > this.response.width || y < 0 || y > this.response.height) {
            return null;
        }

        const pixel = this.response.data[(this.response.height - y) * this.response.width + x];

        if (pixel === 0) {
            return null;
        }

        return {
            product: products[this.productId],
            palette: this.palette.id,
            objects: [
                {
                    type: 'raster',
                    value: pixel,
                    palette: this.palette
                }
            ]
        };
    }
}
